import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatNumber, formatPct } from '../../Shared/Formatters';

/**
 * Generic table widget — renders a { columns, rows } contract.
 *
 * Used by:
 *   - yoy_growth  (server returns { columns, rows })
 *   - flight_risk_table / any `table` chart_type that returns a plain row array
 *     (columns are inferred from the first row's keys, id-like keys dropped)
 *
 * Column shape: { key, label, format?: 'money' | 'percent' | 'number' | 'text' }
 */
export default function TableWidget({ data }) {
    const { t } = useTranslation();
    if (!data?.data) return null;
    const payload = data.data;

    const rows = Array.isArray(payload) ? payload : (payload.rows || []);
    if (!rows.length) return <div style={{ padding: 20 }} className="v3-body">{t('No data')}</div>;

    const columns = (!Array.isArray(payload) && payload.columns) || inferColumns(rows[0]);

    return (
        <div style={{ padding: '4px 12px 12px', overflow: 'auto', height: '100%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                    <tr>
                        {columns.map(col => (
                            <th
                                key={col.key}
                                className="v3-label"
                                style={{
                                    textAlign: col.format && col.format !== 'text' ? 'right' : 'left',
                                    padding: '6px 8px',
                                    borderBottom: '1px solid var(--v3-border, rgba(255,255,255,0.08))',
                                    whiteSpace: 'nowrap',
                                }}
                            >{t(col.label)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            {columns.map(col => {
                                const raw = r[col.key];
                                const isNum = col.format && col.format !== 'text';
                                return (
                                    <td
                                        key={col.key}
                                        className="v3-body"
                                        style={{
                                            textAlign: isNum ? 'right' : 'left',
                                            padding: '6px 8px',
                                            borderBottom: '1px solid var(--v3-border, rgba(255,255,255,0.05))',
                                            fontFamily: isNum ? 'JetBrains Mono, monospace' : undefined,
                                            whiteSpace: 'nowrap',
                                            color: col.format === 'percent' ? growthColor(raw) : undefined,
                                        }}
                                    >{formatCell(raw, col.format)}</td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function formatCell(val, format) {
    if (val === null || val === undefined || val === '') return '—';
    switch (format) {
        case 'money':   return formatMoney(Number(val));
        case 'number':  return formatNumber(Number(val));
        case 'percent': return formatPct(Number(val)); // already prefixes +/-
        default:        return String(val);
    }
}

function growthColor(val) {
    const n = Number(val);
    if (!Number.isFinite(n) || n === 0) return 'var(--v3-text-muted)';
    return n > 0 ? 'var(--v3-text-success, #10B981)' : 'var(--v3-red, #EF4444)';
}

// Infer columns from a plain row object: keep readable keys, drop id-like ones.
function inferColumns(sample) {
    return Object.keys(sample || {})
        .filter(k => !/(^_?id$|_id$)/i.test(k))
        .map(k => ({
            key: k,
            label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            format: typeof sample[k] === 'number' ? 'number' : 'text',
        }));
}
